/**
 * PersonaGrid — Reusable character selection grid with audio preview.
 * Used in: VoiceCharacterSelector, SettingsDialog, OnboardingWizard, VoiceSettingsModal (admin).
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { COARSE_HIT_ALONE } from '@/lib/touch-target';
import { usePersonaPreferences } from '@/hooks/usePersonaPreferences';
import type { CoachCharacterInfo, PersonaArchetype, PersonaId } from '@/services/settingsService';

// Persona identity colors — one of the two sanctioned identity exemptions
// (design-grammar §3). The `text` field each row once carried was dead code
// (declared, never rendered) and was deleted under #747 — which is why this
// table is gradients + borders only; do not re-add per-archetype text colors.
const ARCHETYPE_COLORS: Record<PersonaArchetype, { bg: string; border: string }> = {
  warm:     { bg: 'from-amber-500/20 to-orange-500/20',  border: 'border-amber-500/40'  },
  grounded: { bg: 'from-sky-500/20 to-cyan-500/20',      border: 'border-sky-500/40'    },
  clear:    { bg: 'from-violet-500/20 to-purple-500/20', border: 'border-violet-500/40' },
};

function getPreviewUrl(characterId: string): string {
  return `/audio/voice-previews/${characterId}.mp3`;
}

interface PersonaGridProps {
  characters: CoachCharacterInfo[];
  selectedId: PersonaId | string | undefined;
  onSelect: (id: PersonaId) => void;
  showPreview?: boolean;
  compact?: boolean;
}

export function PersonaGrid({
  characters,
  selectedId,
  onSelect,
  showPreview = true,
  compact = false,
}: PersonaGridProps) {
  const { t } = useTranslation('chat');
  const { preferences } = usePersonaPreferences();
  const speedAdjustment = preferences?.speedAdjustment ?? 0;
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const handlePlay = (id: string) => {
    if (playingId === id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
    }
    const audio = new Audio(getPreviewUrl(id));
    audio.playbackRate = Math.max(0.5, Math.min(4.0, 1.0 + speedAdjustment));
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(id);
  };

  return (
    <div
      role="group"
      className={cn('grid gap-3', compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2')}
    >
      {characters.map((character) => {
        const isSelected = selectedId === character.id;
        const isPlaying = playingId === character.id;
        const colors = ARCHETYPE_COLORS[character.archetype] || ARCHETYPE_COLORS.grounded;

        return (
          /* #1295 — `role="group"` + `aria-pressed`, NOT `radiogroup`/`radio`.
             The radio roles promised the ARIA radio keyboard pattern (arrow
             keys move the selection, one Tab stop per group) and this
             component implemented neither: arrows were inert and every card
             was its own Tab stop. Same fix as f8fdade6 (#1054/B-500) — drop
             the claim rather than build roving tabindex. The card is now a
             real <button>, so the preview control is rendered as a sibling
             (absolutely positioned) rather than nested inside it — a button
             inside a button is invalid HTML and unreachable to AT. */
          <div key={character.id} className="relative">
            <button
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(character.id as PersonaId)}
              className={cn(
                'w-full text-left rounded-lg border p-3 transition-all cursor-pointer',
                `bg-gradient-to-br ${colors.bg}`,
                isSelected
                  ? `${colors.border} ring-1 ring-primary`
                  : 'border-border hover:border-primary/50'
              )}
            >
              {isSelected && (
                <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="h-2.5 w-2.5" />
                </span>
              )}

              {showPreview && (
                // Reserves the same layout space the sibling preview button
                // occupies, so the card's text doesn't reflow under it.
                <div className="mb-2 h-7 w-7" aria-hidden="true" />
              )}

              <p className={cn('font-medium leading-snug', compact ? 'text-caption' : 'text-body')}>
                {t(`personas.${character.id}.name` as never, { defaultValue: character.name })}
              </p>
              <p className={cn('mt-0.5 text-stevens-ink-3 leading-snug', compact ? 'text-meta' : 'text-caption')}>
                {t(`personas.${character.id}.tagline` as never, { defaultValue: character.tagline })}
              </p>
            </button>

            {showPreview && (
              <button
                type="button"
                onClick={() => handlePlay(character.id)}
                className={cn(
                  // #2603 (D-2420): 28 px drawn, 44 px reach on a coarse pointer;
                  // the reach lies over the card's own corner, which this
                  // button already sits on top of.
                  //
                  // #2643 (d): COARSE_HIT_ALONE carries `relative`, and `cn`
                  // (tailwind-merge) keeps the LAST position class. Listed
                  // after `absolute` it won, so the button fell into the flow
                  // below its card, 12 px down, where the next row's card
                  // covered its lower edge. `absolute` is already the ::after's
                  // containing block, so it comes last.
                  COARSE_HIT_ALONE,
                  '[@media(pointer:coarse)]:after:-inset-2',
                  'absolute top-3 left-3 flex h-7 w-7 items-center justify-center rounded-full border transition-colors',
                  isPlaying
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background hover:border-primary hover:text-primary'
                )}
                aria-label={isPlaying ? t('voiceSetup.pausePreview') : t('voiceSetup.playPreview')}
              >
                {isPlaying ? (
                  <Pause className="h-3 w-3" />
                ) : (
                  <Play className="h-3 w-3 ml-0.5" />
                )}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
